import React from 'react'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { DataTable, DrillLinks, KpiGrid, KpiTile, Section, Unavailable } from './ManagementWidgets'
import { applyTextFilters, exportToSpreadsheet, flattenMetrics, metricNumber, metricText } from './managementUtils'

const LINKS = [
  { label: 'Invoice ageing', to: '/accounts/reports/invoice-aging' },
  { label: 'Outstanding payments', to: '/accounts/reports/outstanding-payments' },
  { label: 'Sundry debtors', to: '/accounts/reports/sundry-debtors' }
]

const BUCKET_COLORS = {
  notDue: '#16a34a',
  d1_7: '#84cc16',
  d8_15: '#eab308',
  d16_30: '#f59e0b',
  d31_60: '#f97316',
  d61_90: '#ef4444',
  d90plus: '#991b1b'
}

export const AgeingBuckets = ({ buckets }) => {
  const entries = Object.entries(buckets || {})
  if (!entries.length) return <Unavailable>No receivables ageing data</Unavailable>
  const total = entries.reduce((s, [, b]) => s + (metricNumber(b.amount) || 0), 0)
  return (
    <>
      {total > 0 && (
        <div className="mis-bar" aria-hidden="true">
          {entries.map(([k, b]) => {
            const v = metricNumber(b.amount) || 0
            return v > 0 ? <span key={k} style={{ width: `${(v / total) * 100}%`, background: BUCKET_COLORS[k] || '#9ca3af' }} title={`${b.label}: ${metricText(b.amount, 'currency')}`} /> : null
          })}
        </div>
      )}
      <KpiGrid>
        {entries.map(([k, b]) => (
          <KpiTile key={k} label={b.label || k} metric={b.amount} format="currency" hint={`${metricText(b.count)} invoices`} />
        ))}
      </KpiGrid>
    </>
  )
}

export const CUSTOMER_COLUMNS = [
  { key: 'customerName', label: 'Customer' },
  { key: 'outstanding', label: 'Outstanding', format: 'currency' },
  { key: 'overdue', label: 'Overdue', format: 'currency' },
  { key: 'invoiceCount', label: 'Invoices', format: 'number' },
  { key: 'oldestDaysPastDue', label: 'Oldest (days past due)', align: 'right' }
]

const CustomerCollectionHealthReport = () => {
  const report = useManagementReport(managementService.getCustomerCollection)
  const { filters } = report

  const onExport = (data) => exportToSpreadsheet('customer-collection', {
    Summary: flattenMetrics({ summary: data.summary, collections: data.collections }),
    Ageing: Object.values(data.buckets || {}).map((b) => ({ Bucket: b.label, Amount: metricText(b.amount, 'currency'), Invoices: metricText(b.count) })),
    Customers: (data.customers || []).map(({ buckets, ...rest }) => rest),
    Invoices: data.invoices || []
  })

  return (
    <ManagementReportShell
      title="Customer Collection Health"
      subtitle="Receivables ageing, DSO, collection efficiency and overdue customers"
      report={report}
      onExport={onExport}
    >
      {(d) => {
        const s = d.summary || {}
        const c = d.collections || {}
        return (
          <>
            <DrillLinks links={LINKS} />
            {d.note && <p className="mis-muted" style={{ marginBottom: 12 }}>{d.note}</p>}
            <Section title="Receivables summary">
              <KpiGrid>
                <KpiTile label="Total outstanding" metric={s.totalOutstanding} format="currency" to="/accounts/reports/invoice-aging" />
                <KpiTile label="Overdue" metric={s.overdue} format="currency" tone="amber" />
                <KpiTile label="Not due" metric={s.notDue} format="currency" />
                <KpiTile label="Overdue %" metric={s.overduePercent} format="percent" />
                <KpiTile label="90+ days" metric={s.over90Days} format="currency" tone="red" />
                <KpiTile label="DSO" metric={s.dsoDays} format="days" />
                <KpiTile label="Open invoices" metric={s.openInvoiceCount} />
                <KpiTile label="Customers overdue" metric={s.customersWithOverdue} />
              </KpiGrid>
            </Section>

            <Section title="Ageing buckets" subtitle="Days past due date">
              <AgeingBuckets buckets={d.buckets} />
            </Section>

            <Section title="Collections (period)">
              <KpiGrid>
                <KpiTile label="Received" metric={c.received} format="currency" />
                <KpiTile label="Cleared" metric={c.cleared} format="currency" />
                <KpiTile label="Payments" metric={c.paymentCount} />
                <KpiTile label="Billed in period" metric={c.billedInPeriod} format="currency" />
                <KpiTile label="Collection / billing" metric={c.collectionToBillingPercent} format="percent" />
                <KpiTile label="Credit notes" metric={d.creditNotes} format="currency" />
                <KpiTile label="Disputed invoices" metric={d.disputedInvoices} />
              </KpiGrid>
            </Section>

            <Section title="Customers by overdue exposure">
              <DataTable rows={applyTextFilters(d.customers, filters)} columns={CUSTOMER_COLUMNS} emptyText="No outstanding receivables." />
            </Section>

            <Section title="Open invoices" subtitle="Oldest first">
              <DataTable
                rows={applyTextFilters(d.invoices, filters)}
                maxRows={20}
                emptyText="No open invoices."
                columns={[
                  { key: 'invoiceNumber', label: 'Invoice' },
                  { key: 'invoiceDate', label: 'Date', type: 'date' },
                  { key: 'dueDate', label: 'Due', type: 'date' },
                  { key: 'customerName', label: 'Customer' },
                  { key: 'projectName', label: 'Project' },
                  { key: 'invoiceAmount', label: 'Amount', format: 'currency' },
                  { key: 'outstanding', label: 'Outstanding', format: 'currency' },
                  { key: 'daysPastDue', label: 'Days past due', align: 'right' }
                ]}
              />
            </Section>
          </>
        )
      }}
    </ManagementReportShell>
  )
}

export default CustomerCollectionHealthReport
